import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, Package, Check, Route, Loader2 } from 'lucide-react'
import api from '../../services/api'
import { todayInManaus, formatDateBR, formatDateLongBR } from '../../utils/dateManaus'
import Card from '../../components/ui/Card'
import Button from '../../components/ui/Button'
import toast from 'react-hot-toast'

export default function RouteCreate() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [selectedDate, setSelectedDate] = useState(
    todayInManaus()
  )
  const [departureTime, setDepartureTime] = useState('08:00')
  const [selectedDeliveries, setSelectedDeliveries] = useState([])
  const [travelMode, setTravelMode] = useState(null)
  const [selectedDriverId, setSelectedDriverId] = useState('')

  const { data: meData } = useQuery({
    queryKey: ['authMe'],
    queryFn: async () => {
      const response = await api.get('/api/auth/me')
      return response.data.data.user
    },
  })

  useEffect(() => {
    if (meData?.travelMode && travelMode === null) {
      setTravelMode(meData.travelMode)
    }
  }, [meData, travelMode])

  // Busca motoristas do vendor (BUG-79) - backend não filtra isActive, então
  // filtramos no client pra não oferecer motorista desabilitado como opção.
  const { data: driversData } = useQuery({
    queryKey: ['drivers'],
    queryFn: async () => {
      const response = await api.get('/api/drivers')
      return response.data.data
    },
  })

  const activeDrivers = (driversData?.drivers || []).filter((driver) => driver.isActive)

  // BUG-101: esconde entrega que já está numa rota (o POST /api/routes recusa
  // route_id preenchido). limit 100 = máximo do listDeliveriesSchema; hasMore
  // sinaliza que a data tem mais entregas aguardando do que a tela trouxe.
  const { data: deliveriesData, isLoading: loadingDeliveries } = useQuery({
    queryKey: ['availableDeliveries', selectedDate],
    queryFn: async () => {
      const response = await api.get('/api/deliveries', {
        params: {
          date: selectedDate,
          status: 'aguardando_saida',
          limit: 100,
        }
      })
      const { deliveries, pagination } = response.data.data
      return {
        deliveries: deliveries.filter((d) => !d.routeId),
        hasMore: Boolean(pagination?.hasNext),
      }
    },
  })

  const availableDeliveries = deliveriesData?.deliveries

  // Mutation para criar rota
  const createRoute = useMutation({
    mutationFn: async (data) => {
      return api.post('/api/routes', data)
    },
    onSuccess: (response) => {
      toast.success('Rota criada e otimizada!')
      queryClient.invalidateQueries({ queryKey: ['routes'] })
      queryClient.invalidateQueries({ queryKey: ['deliveries'] })
      queryClient.invalidateQueries({ queryKey: ['availableDeliveries'] })
      navigate(`/routes/${response.data.data.route.id}`)
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Erro ao criar rota')
    },
  })

  const updateTravelMode = useMutation({
    mutationFn: async (newMode) => {
      return api.patch('/api/vendors/travel-mode', { travelMode: newMode })
    },
    onSuccess: (_, newMode) => {
      setTravelMode(newMode)
      toast.success('Modo de transporte atualizado')
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Erro ao atualizar modo de transporte')
    },
  })

  const handleTravelModeChange = (e) => {
    updateTravelMode.mutate(e.target.value)
  }

  // Toggle seleção de entrega
  const toggleDelivery = (deliveryId) => {
    setSelectedDeliveries((prev) => {
      if (prev.includes(deliveryId)) {
        return prev.filter((id) => id !== deliveryId)
      }
      return [...prev, deliveryId]
    })
  }

  // Selecionar todas
  const selectAll = () => {
    if (availableDeliveries) {
      setSelectedDeliveries(availableDeliveries.map((d) => d.id))
    }
  }

  // Desmarcar todas
  const deselectAll = () => {
    setSelectedDeliveries([])
  }

  // Criar rota
  const handleCreateRoute = () => {
    if (selectedDeliveries.length < 1) {
      toast.error('Selecione pelo menos 1 entrega para criar uma rota')
      return
    }

    createRoute.mutate({
      scheduledDate: selectedDate,
      departureTime: departureTime,
      deliveryIds: selectedDeliveries,
      driverId: selectedDriverId || null,
    })
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button 
          onClick={() => navigate('/routes')}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="page-title">Nova Rota</h1>
          <p className="page-subtitle">
            Selecione as entregas para criar uma rota otimizada.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Coluna principal - Lista de entregas */}
        <div className="lg:col-span-2">
          <Card>
            {/* Filtro de data e ações */}
            <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
              <div className="flex items-center gap-4 flex-wrap">
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium text-gray-700">Data:</label>
                  <input
                    type="date"
                    value={selectedDate}
                    onChange={(e) => {
                      setSelectedDate(e.target.value)
                      setSelectedDeliveries([])
                    }}
                    className="input-field w-auto"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium text-gray-700">Hora de saída:</label>
                  <input
                    type="time"
                    value={departureTime}
                    onChange={(e) => setDepartureTime(e.target.value)}
                    className="input-field w-auto"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium text-gray-700">Modo de transporte:</label>
                  <select
                    value={travelMode ?? ''}
                    onChange={handleTravelModeChange}
                    disabled={travelMode === null || updateTravelMode.isPending}
                    className="input-field w-auto"
                  >
                    <option value="" disabled>Carregando...</option>
                    <option value="DRIVE">Carro</option>
                    <option value="TWO_WHEELER">Moto</option>
                    <option value="BICYCLE">Bicicleta</option>
                  </select>
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium text-gray-700">Motorista:</label>
                  <select
                    value={selectedDriverId}
                    onChange={(e) => setSelectedDriverId(e.target.value)}
                    className="input-field w-auto"
                  >
                    <option value="">Sem motorista atribuído</option>
                    {activeDrivers.map((driver) => (
                      <option key={driver.id} value={driver.id}>
                        {driver.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={selectAll}
                  className="text-sm text-primary-600 hover:underline"
                >
                  Selecionar todas
                </button>
                <span className="text-gray-300">|</span>
                <button
                  onClick={deselectAll}
                  className="text-sm text-gray-500 hover:underline"
                >
                  Desmarcar todas
                </button>
              </div>
            </div>

            {deliveriesData?.hasMore && (
              <div className="mb-4 p-3 rounded-lg bg-amber-100 text-amber-700 text-sm">
                Esta data tem mais de 100 entregas aguardando saída. Algumas podem não aparecer nesta lista.
              </div>
            )}

            {/* Lista de entregas */}
            {loadingDeliveries ? (
              <div className="text-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary-600 mx-auto" />
                <p className="text-gray-500 mt-2">Carregando entregas...</p>
              </div>
            ) : availableDeliveries?.length === 0 ? (
              <div className="text-center py-12">
                <Package className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 mb-4">
                  Nenhuma entrega pendente para {formatDateBR(selectedDate)}
                </p>
                <button
                  onClick={() => navigate('/deliveries/new')}
                  className="text-primary-600 hover:underline"
                >
                  Criar nova entrega
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                {availableDeliveries?.map((delivery) => (
                  <button
                    key={delivery.id}
                    onClick={() => toggleDelivery(delivery.id)}
                    className={`w-full text-left p-4 rounded-lg border-2 transition-colors ${
                      selectedDeliveries.includes(delivery.id)
                        ? 'border-primary-600 bg-primary-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                          selectedDeliveries.includes(delivery.id)
                            ? 'border-primary-600 bg-primary-600'
                            : 'border-gray-300'
                        }`}>
                          {selectedDeliveries.includes(delivery.id) && (
                            <Check className="w-4 h-4 text-white" />
                          )}
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">
                            {delivery.recipient?.name}
                          </p>
                          <p className="text-sm text-gray-500">
                            {delivery.recipient?.address?.street || delivery.recipient?.addressStreet}, {delivery.recipient?.address?.number || delivery.recipient?.addressNumber} - {delivery.recipient?.address?.neighborhood || delivery.recipient?.addressNeighborhood}
                          </p>
                        </div>
                      </div>
                      <span className="text-xs text-gray-400">
                        {delivery.trackingCode}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </Card>
        </div>

        {/* Coluna lateral - Resumo */}
        <div>
          <Card className="sticky top-24">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 bg-primary-100 rounded-lg">
                <Route className="w-6 h-6 text-primary-600" />
              </div>
              <div>
                <p className="font-semibold text-gray-900">Resumo da Rota</p>
                <p className="text-sm text-gray-500">
                  {formatDateLongBR(selectedDate)}
                </p>
              </div>
            </div>

            <div className="space-y-4 mb-6">
              <div className="flex justify-between items-center py-3 border-b border-gray-100">
                <span className="text-gray-600">Entregas selecionadas</span>
                <span className="font-semibold text-gray-900">
                  {selectedDeliveries.length}
                </span>
              </div>
              <div className="flex justify-between items-center py-3 border-b border-gray-100">
                <span className="text-gray-600">Status</span>
                <span className="text-sm text-gray-500">
                  {selectedDeliveries.length < 1 
                    ? 'Selecione ao menos 1' 
                    : 'Pronto para otimizar'}
                </span>
              </div>
            </div>

            <Button
              fullWidth
              onClick={handleCreateRoute}
              isLoading={createRoute.isPending}
              disabled={selectedDeliveries.length < 1}
            >
              {createRoute.isPending ? 'Otimizando...' : 'Criar e Otimizar Rota'}
            </Button>

            <p className="text-xs text-gray-500 text-center mt-4">
              A rota será otimizada automaticamente usando o Google Routes API.
            </p>
          </Card>
        </div>
      </div>
    </div>
  )
}
